import { CrudFilter, useInfiniteList } from '@refinedev/core';
import { useMemo } from 'react';
import type { CalendarEvent } from 'src/meetings/models';

type CalendarEventsInput = {
  pastOrUpcoming: 'PAST' | 'UPCOMING';
  clientMeetingsOnly?: boolean;
  clientId?: string;
};

export const useCalendarEvents = ({
  pastOrUpcoming,
  clientMeetingsOnly = false,
  clientId,
}: CalendarEventsInput) => {
  const startOfMinute = new Date();
  startOfMinute.setSeconds(0, 0);
  const filters = useMemo(
    () => buildFilters(pastOrUpcoming, startOfMinute, clientMeetingsOnly, clientId),
    [pastOrUpcoming, startOfMinute, clientMeetingsOnly, clientId],
  );

  const resource = clientId ? `clients/${clientId}/calendar-events` : 'calendar-events';

  const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteList<CalendarEvent>({
      resource,
      filters,
      pagination: { pageSize: 10 },
    });

  const allPages = (data?.pages ?? []).flatMap(page => page.data);

  return {
    events: allPages,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

function buildFilters(
  pastOrUpcoming: 'PAST' | 'UPCOMING',
  date: Date,
  clientMeetingsOnly: boolean,
  clientId?: string,
): CrudFilter[] {
  const baseFilters: CrudFilter[] = [
    {
      field: 'endAt',
      operator: pastOrUpcoming === 'PAST' ? 'lt' : 'gte',
      value: date.toISOString(),
    },
    {
      field: 'order',
      operator: 'eq',
      value: pastOrUpcoming === 'PAST' ? 'desc' : 'asc',
    },
  ];

  if (!clientId) {
    baseFilters.push({
      field: 'clientMeetingsOnly',
      operator: 'eq',
      value: clientMeetingsOnly,
    });
  }

  return baseFilters;
}
